import React from 'react';
import {
  Paper,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { Print as PrintIcon, Business as BusinessIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { SalesSummaryReport as SalesSummaryReportType } from '../../types';

interface SalesSummaryReportProps {
  report: SalesSummaryReportType;
  onPrint?: () => void;
  selectedBranchName?: string;
}

const SalesSummaryReport: React.FC<SalesSummaryReportProps> = ({           
  report, 
  onPrint,
  selectedBranchName 
}) => {
  const theme = useTheme();
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EG', { 
      style: 'currency', 
      currency: 'EGP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-EG').format(num);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Calculate derived metrics
  const totalRevenue = report.totalSales;
  const totalDiscount = report.totalDiscountOfCashOrders + report.totalDiscountOfVisaOrders;
  const cancellationRate = ((report.countOfCancelledOrders / report.countOfAllOrders) * 100).toFixed(2);
  const avgOrderValue = report.countOfAllOrders > 0 ? (totalRevenue / report.countOfAllOrders) : 0;

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <div className="print-area">
      {/* Unified Print & Screen Styles */}
      <style>
        {`
          /* Common styles for both print and screen */
          .print-area {
            width: 100%;
            max-width: 100vw;
            margin: 0;
            padding: 0;
            background: ${theme.palette.background.paper};
            font-family: 'Arial', sans-serif;
            font-size: 14px;
            line-height: 1.4;
            color: ${theme.palette.text.primary};
            min-height: 100vh;
            box-sizing: border-box;
            overflow-x: auto;
          }
          
          .print-header {
            border-bottom: 2px solid ${theme.palette.text.primary};
            margin-bottom: 8px;
            padding: 2px 15px 5px 15px;
            page-break-inside: avoid;
          }
          
          .print-header h1 {
            font-size: 24px;
            font-weight: bold;
            color: ${theme.palette.text.primary};
            margin: 0;
            text-align: center;
          }
          
          .report-meta {
            background-color: transparent;
            padding: 10px 15px;
            border: none;
            font-size: 14px;
            margin-bottom: 20px;
            border-radius: 0;
          }
          
          .report-filters {
            margin-bottom: 20px;
          }
          
          .print-section {
            page-break-inside: avoid;
            margin-bottom: 20px;
            border: none;
            padding: 0 15px;
            background: transparent;
          }
          
          .section-title {
            font-size: 24px; /* Larger section titles */
            font-weight: bold;
            color: ${theme.palette.text.primary};
            margin: 30px 0 20px 0;
            padding: 15px 0 10px 0;
            border-bottom: 3px solid ${theme.palette.text.primary};
          }
          
          .metric-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            margin-bottom: 30px;
            justify-content: flex-start;
          }
          .metric-item {
            flex: 1 1 180px;
            min-width: 180px;
            max-width: 220px;
            text-align: center;
            border: 1px solid ${theme.palette.divider};
            padding: 18px 8px;
            background: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.background.paper} !important;
            border-radius: 10px;
            word-break: break-word;
            overflow-wrap: break-word;
            box-sizing: border-box;
            box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'};
          }
          .metric-value {
            font-size: 22px;
            font-weight: bold;
            color: ${theme.palette.text.primary};
            margin-bottom: 8px;
            word-break: break-all;
          }
          .metric-label {
            font-size: 13px;
            color: ${theme.palette.text.secondary};
            font-weight: 600;
          }
          
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            font-size: 14px; /* Larger table font */
            page-break-inside: avoid;
            table-layout: auto; /* Auto layout for full screen */
          }
          
          .print-table th {
            background-color: ${theme.palette.action.hover};
            font-weight: bold;
            padding: 15px 10px; /* More generous padding */
            border: 2px solid ${theme.palette.text.primary};
            text-align: left;
            font-size: 14px;
            word-wrap: break-word;
            color: ${theme.palette.text.primary};
          }
          
          .print-table td {
            padding: 12px 8px; /* More generous padding */
            border: 1px solid ${theme.palette.divider};
            font-size: 13px;
            word-wrap: break-word;
            text-align: left;
            color: ${theme.palette.text.primary};
          }
          
          .print-table .number {
            text-align: right;
          }
          
          .total-row {
            background-color: ${theme.palette.action.selected};
            font-weight: bold;
          }
          
          .summary-box {
            border: 1px solid ${theme.palette.text.primary};
            padding: 12px;
            margin: 8px 0;
            background-color: ${theme.palette.background.default};
            page-break-inside: avoid;
            font-size: 11px;
            color: ${theme.palette.text.primary};
          }
          
          .summary-box-title {
            font-weight: bold;
            font-size: 13px;
            margin-bottom: 8px;
            color: ${theme.palette.text.primary};
          }
          
          .summary-grid {
            display: flex;
            gap: 25px; /* More spacing for full screen */
            margin-bottom: 30px;
            flex-wrap: wrap;
          }
          
          .summary-item {
            flex: 1;
            min-width: 300px; /* Larger summary boxes */
          }
          
          .summary-box {
            border: 2px solid ${theme.palette.divider};
            padding: 20px; /* More padding */
            margin: 10px 0;
            background-color: transparent;
            page-break-inside: avoid;
            font-size: 14px;
            border-radius: 8px;
          }
          
          .summary-box-title {
            font-weight: bold;
            font-size: 18px; /* Larger titles */
            margin-bottom: 12px;
            color: ${theme.palette.text.primary};
          }
          
          .page-footer {
            margin-top: 30px;
            padding-top: 15px;
            text-align: center;
            font-size: 10px;
            color: ${theme.palette.text.secondary};
            border-top: 1px solid ${theme.palette.divider};
          }
          
          /* Hide elements that shouldn't appear in either view */
          .no-print {
            display: none;
          }
          
          /* Screen-specific adjustments */
          @media screen {
            .print-area {
              background: ${theme.palette.background.default};
              padding: 0;
            }
            
            .no-print {
              display: block;
              margin-bottom: 15px;
              padding: 0 15px;
            }
            
            /* Add some visual enhancement for screen */
            .metric-item {
              background: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.background.paper} !important;
              box-shadow: 0 4px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'};
              transition: transform 0.2s ease;
            }
            
            .metric-item:hover {
              transform: translateY(-2px);
            }
            
            .summary-box {
              box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'};
            }
            
            .print-table {
              box-shadow: 0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'};
              overflow: hidden;
            }
          }
          
          /* Print-specific adjustments */
          @media print {
            @page {
              margin: 8mm;
              size: A4 portrait;
            }
            
            /* Hide navigation elements but keep content visible */
            nav, .sidebar, .menu, .navigation, .drawer, .left-panel, 
            [class*="sidebar"], [class*="menu"], [class*="nav"], 
            [class*="drawer"], [role="navigation"] {
              display: none !important;
            }
            
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 100% !important;
              height: auto !important;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box !important;
              color: black !important;
              background: white !important;
            }
            
            .print-area {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              box-shadow: none !important;
              border: none !important;
              overflow: visible !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              font-size: 12px !important;
              line-height: 1.3 !important;
              color: black !important;
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            .print-header {
              margin-bottom: 5mm !important;
              border-bottom: 2px solid black !important;
              padding: 2mm !important;
              page-break-after: auto !important;
              color: black !important;
            }
            
            .report-filters {
              margin-bottom: 2mm !important;
              font-size: 9px !important;
              padding: 1mm !important;
              border-bottom: 1px solid #dee2e6 !important;
              background: none !important;
              border: none !important;
            }
            
            .print-header h1 {
              font-size: 18px !important;
              margin: 0 0 5mm 0 !important;
              text-align: center !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .print-header .app-name {
              font-size: 12px !important;
              margin-bottom: 2mm !important;
              text-align: left !important;
              font-weight: 600 !important;
              color: black !important;
            }
            
            .section-title {
              font-size: 14px !important;
              margin: 8mm 0 4mm 0 !important;
              padding: 2mm 0 !important;
              border-bottom: 2px solid black !important;
              font-weight: bold !important;
              page-break-after: auto !important;
              page-break-before: auto !important;
              color: black !important;
            }
            
            .metric-grid {
              display: flex !important;
              flex-wrap: wrap !important;
              gap: 3mm !important;
              margin-bottom: 8mm !important;
              justify-content: space-between !important;
            }
            
            .metric-item {
              flex: 1 1 auto !important;
              min-width: 35mm !important;
              max-width: 45mm !important;
              padding: 3mm !important;
              border: 1px solid black !important;
              background: white !important;
              text-align: center !important;
              box-sizing: border-box !important;
              color: black !important;
            }
            
            .metric-value {
              font-size: 14px !important;
              font-weight: bold !important;
              margin-bottom: 2mm !important;
              word-break: break-word !important;
              color: black !important;
            }
            
            .metric-label {
              font-size: 8px !important;
              line-height: 1.2 !important;
              color: black !important;
            }
            
            .print-table {
              width: 100% !important;
              border-collapse: collapse !important;
              margin-bottom: 6mm !important;
              font-size: 10px !important;
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              color: black !important;
              background: white !important;
            }
            
            .print-table th {
              background-color: #f0f0f0 !important;
              font-weight: bold !important;
              padding: 2mm 1mm !important;
              border: 1px solid black !important;
              text-align: left !important;
              font-size: 9px !important;
              color: black !important;
            }
            
            .print-table td {
              padding: 1.5mm 1mm !important;
              border: 1px solid black !important;
              font-size: 8px !important;
              color: black !important;
              background: white !important;
            }
            
            .print-table .number {
              text-align: right !important;
            }
            
            .total-row {
              background-color: #e0e0e0 !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .summary-grid {
              display: flex !important;
              gap: 3mm !important;
              margin-bottom: 6mm !important;
              flex-wrap: wrap !important;
            }
            
            .summary-item {
              flex: 1 !important;
              min-width: 70mm !important;
            }
            
            .summary-box {
              border: 1px solid black !important;
              padding: 3mm !important;
              background-color: white !important;
              font-size: 9px !important;
              color: black !important;
            }
            
            .summary-box-title {
              font-weight: bold !important;
              font-size: 10px !important;
              margin-bottom: 2mm !important;
              color: black !important;
            }
            
            .page-footer {
              margin-top: 10mm !important;
              font-size: 8px !important;
              text-align: center !important;
              border-top: 1px solid #ccc !important;
              padding-top: 3mm !important;
            }
            
            /* Allow content to break across pages */
            .print-section {
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              margin-bottom: 6mm !important;
              break-inside: auto !important;
              orphans: 2 !important;
              widows: 2 !important;
            }
            
            /* Ensure all sections can break to new pages */
            .executive-summary,
            .sales-performance,
            .cancellation-analysis,
            .discount-analysis {
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              break-inside: auto !important;
              orphans: 2 !important;
              widows: 2 !important;
            }
            
            /* Force some sections to start on new page if needed */
            .cancellation-analysis {
              page-break-before: auto !important;
            }
            
            /* Try explicit page break */
            .sales-performance {
              page-break-after: auto !important;
            }
            
            /* Add a page break helper class */
            .page-break {
              page-break-before: always !important;
              break-before: page !important;
            }
            
            /* Enhanced table breaking */
            .print-table tbody {
              page-break-inside: auto !important;
              break-inside: auto !important;
            }
            
            .print-table tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-after: auto !important;
            }
            
            /* Content flow control */
            p, div {
              orphans: 2 !important;
              widows: 2 !important;
            }
            
            /* Ensure content doesn't get constrained */
            * {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
            }
          }
        `}
      </style>

      {/* Header Section */}
      <div className="print-header">
        <div style={{ marginBottom: '10px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div className="app-name" style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.primary.main,
              letterSpacing: '1px'
            }}>
              Dining-X
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.text.primary,
              letterSpacing: '1px'
            }}>
              {report.restaurantName || 'Restaurant Name'}
            </div>
          </div>
          <h1 style={{ fontWeight: 'bold', color: theme.palette.text.primary, margin: 0, textAlign: 'center' }}>
            Sales Summary Report
          </h1>
        </div>
      </div>

      {/* Filter Information */}
      <div className="report-filters">
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '5px 15px',
          margin: '0 15px 10px 15px',
          fontSize: '14px',
          minHeight: 'auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary
        }}>
          <span><strong>Period:</strong> {formatDateShort(report.periodStart)} - {formatDateShort(report.periodEnd)}</span>
          <span><strong>Branch:</strong> {selectedBranchName || 'All Branches'}</span>
          <span><strong>Currency:</strong> EGP</span>
        </div>
      </div>

      {/* Print Button */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          variant="contained"
          size="large"
          sx={{ minWidth: 150 }}
        >
          Print Report
        </Button>
      </div>

      {/* Executive Summary */}
      <div className="print-section executive-summary">
        <div className="section-title">
          EXECUTIVE SUMMARY
        </div>
        <div className="metric-grid">
          <div className="metric-item">
            <div className="metric-value">{formatNumber(report.countOfAllOrders)}</div>
            <div className="metric-label">Total Orders</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{formatCurrency(report.totalSales)}</div>
            <div className="metric-label">Total Sales</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{formatCurrency(avgOrderValue)}</div>
            <div className="metric-label">Avg Order Value</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{cancellationRate}%</div>
            <div className="metric-label">Cancellation Rate</div>
          </div>
        </div>
      </div>

      {/* Sales Performance */}
      <div className="print-section sales-performance">
        <div className="section-title">
          1. SALES PERFORMANCE
        </div>
        
        <table className="print-table">
          <thead>
            <tr>
              <th>Payment Method</th>
              <th className="number">Orders Count</th>
              <th className="number">Total Amount</th>
              <th className="number">Percentage</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cash Payments</td>
              <td className="number">{formatNumber(report.countOfCashOrders)}</td>
              <td className="number">{formatCurrency(report.totalCashOrders)}</td>
              <td className="number">{((report.totalCashOrders / totalRevenue) * 100).toFixed(1)}%</td>
            </tr>
            <tr>
              <td>Visa/Card Payments</td>
              <td className="number">{formatNumber(report.countOfVisaOrders)}</td>
              <td className="number">{formatCurrency(report.totalVisaOrders)}</td>
              <td className="number">{((report.totalVisaOrders / totalRevenue) * 100).toFixed(1)}%</td>
            </tr>
            <tr className="total-row">
              <td><strong>TOTAL SALES</strong></td>
              <td className="number"><strong>{formatNumber(report.countOfAllOrders)}</strong></td>
              <td className="number"><strong>{formatCurrency(totalRevenue)}</strong></td>
              <td className="number"><strong>100.0%</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Order Cancellations Analysis */}
      <div className="print-section cancellation-analysis">
        <div style={{pageBreakBefore: 'auto'}}></div>
        <div className="section-title">
          2. ORDER CANCELLATIONS ANALYSIS
        </div>
        
        <table className="print-table">
          <thead>
            <tr>
              <th>Cancellation Type</th>
              <th className="number">Count</th>
              <th className="number">Amount</th>
              <th className="number">% of Total Orders</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cancelled Before Close</td>
              <td className="number">{formatNumber(report.countOfCancelledBeforeCloseOrders)}</td>
              <td className="number">{formatCurrency(report.totalCancelledBeforeCloseOrders)}</td>
              <td className="number">{((report.countOfCancelledBeforeCloseOrders / report.countOfAllOrders) * 100).toFixed(2)}%</td>
            </tr>
            <tr>
              <td>Cancelled After Close</td>
              <td className="number">{formatNumber(report.countOfCancelledAfterClosedOrders)}</td>
              <td className="number">{formatCurrency(report.totalCancelledAfterClosedOrders)}</td>
              <td className="number">{((report.countOfCancelledAfterClosedOrders / report.countOfAllOrders) * 100).toFixed(2)}%</td>
            </tr>
            <tr className="total-row">
              <td><strong>TOTAL CANCELLATIONS</strong></td>
              <td className="number"><strong>{formatNumber(report.countOfCancelledOrders)}</strong></td>
              <td className="number"><strong>{formatCurrency(report.totalCancelledOrders)}</strong></td>
              <td className="number"><strong>{cancellationRate}%</strong></td>
            </tr>
          </tbody>
        </table>

        {/* Cancellation Breakdown by Payment Method */}
        <div className="section-title" style={{marginTop: '15px', fontSize: '14px', borderBottom: `1px solid ${theme.palette.divider}`}}>
          Cancellations by Payment Method
        </div>
        <table className="print-table">
          <thead>
            <tr>
              <th>Payment Method</th>
              <th className="number">Cancelled Count</th>
              <th className="number">Cancelled Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cash Orders Cancelled</td>
              <td className="number">{formatNumber(report.countOfCashCancelledOrders)}</td>
              <td className="number">{formatCurrency(report.totalCashCancelledOrders)}</td>
            </tr>
            <tr>
              <td>Visa Orders Cancelled</td>
              <td className="number">{formatNumber(report.countOfVisaCancelledOrders)}</td>
              <td className="number">{formatCurrency(report.totalVisaCancelledOrders)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Discount Analysis */}
      <div className="print-section discount-analysis">
        
        <div className="section-title">
          3. DISCOUNT ANALYSIS
        </div>
        
        <table className="print-table">
          <thead>
            <tr>
              <th>Payment Method</th>
              <th className="number">Orders with Discount</th>
              <th className="number">Total Discount</th>
              <th className="number">Avg Discount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cash Orders</td>
              <td className="number">{formatNumber(report.countOfCashOrdersWithDiscount)}</td>
              <td className="number">{formatCurrency(report.totalDiscountOfCashOrders)}</td>
              <td className="number">
                {report.countOfCashOrdersWithDiscount > 0 
                  ? formatCurrency(report.totalDiscountOfCashOrders / report.countOfCashOrdersWithDiscount)
                  : formatCurrency(0)
                }
              </td>
            </tr>
            <tr>
              <td>Visa Orders</td>
              <td className="number">{formatNumber(report.countOfVisaOrdersWithDiscount)}</td>
              <td className="number">{formatCurrency(report.totalDiscountOfVisaOrders)}</td>
              <td className="number">
                {report.countOfVisaOrdersWithDiscount > 0 
                  ? formatCurrency(report.totalDiscountOfVisaOrders / report.countOfVisaOrdersWithDiscount)
                  : formatCurrency(0)
                }
              </td>
            </tr>
            <tr className="total-row">
              <td><strong>TOTAL DISCOUNTS</strong></td>
              <td className="number">
                <strong>{formatNumber(report.countOfCashOrdersWithDiscount + report.countOfVisaOrdersWithDiscount)}</strong>
              </td>
              <td className="number"><strong>{formatCurrency(totalDiscount)}</strong></td>
              <td className="number">
                <strong>
                  {(report.countOfCashOrdersWithDiscount + report.countOfVisaOrdersWithDiscount) > 0 
                    ? formatCurrency(totalDiscount / (report.countOfCashOrdersWithDiscount + report.countOfVisaOrdersWithDiscount))
                    : formatCurrency(0)
                  }
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Enhanced Footer */}
      <div className="page-footer">
        <div style={{borderTop: `2px solid ${theme.palette.divider}`, paddingTop: '10px', marginTop: '40px'}}>
          <p style={{margin: '5px 0'}}>
            <strong>Sales Summary Report</strong> | Generated: {format(new Date(), 'MMM dd, yyyy HH:mm')}
          </p>
          <p style={{margin: '5px 0'}}>
            Period: {formatDate(report.periodStart)} - {formatDate(report.periodEnd)} | 
            Total Orders: {formatNumber(report.countOfAllOrders)} | 
            Revenue: {formatCurrency(totalRevenue)}
          </p>
          <p style={{margin: '5px 0', fontSize: '10px', color: theme.palette.text.secondary}}>
            This document contains confidential business information. Distribution should be limited to authorized personnel only.
          </p>
          <p style={{margin: '5px 0', fontSize: '10px', color: theme.palette.text.secondary}}>
            Report generated on {format(new Date(), 'EEEE, MMMM dd, yyyy \'at\' HH:mm:ss')} | 
            © 2025 Business Intelligence Dashboard - Confidential
          </p>
        </div>
      </div>
    </div>
  );
};

export default SalesSummaryReport;